<h1 mat-dialog-title class="review-title">
    <span class="review-title-text">
        <span>{{ documentName }}</span>
        <span class="review-patient" *ngIf="patientName">{{ patientName }}</span>
    </span>

    <!--
        Only drawn when the caller supplied a flag context — the Inbox does, the patient-file
        documents panel does not. See `DocumentReviewDialogData.flag`.
    -->
    <button mat-icon-button class="review-flag" *ngIf="canFlag" type="button"
        (click)="toggleFlag()" [disabled]="flagBusy" data-test="review-flag"
        [matTooltip]="flagged ? 'Clear flag' : 'Flag for attention'"
        [attr.aria-label]="flagged ? 'Clear flag' : 'Flag for attention'"
        [attr.aria-pressed]="flagged">
        <mat-icon>{{ flagged ? 'flag' : 'outlined_flag' }}</mat-icon>
    </button>
</h1>

<div mat-dialog-content class="review-content">

    <div class="review-loading" *ngIf="loading">
        <mat-spinner color="primary" mode="indeterminate" diameter="24"></mat-spinner>
    </div>

    <p class="review-error" *ngIf="!loading && loadFailed">
        This document could not be loaded. It may have been withdrawn, or it may belong to another
        practice.
    </p>

    <ng-container *ngIf="!loading && request">

        <!--
            THE PAGES COME FIRST.

            A reviewer is being asked whether this is the right document, complete and legible.
            Everything else on this dialog is about that judgement, so the thing being judged is
            not put below a form.
        -->
        <app-file-gallery *ngIf="gallery.length" data-test="review-gallery"
            [fileObjects]="gallery" [enableEditing]="false"></app-file-gallery>

        <!--
            The active files the gallery cannot draw. A PDF that survived the return path's
            conversion means the conversion FAILED, and a file with no url means the stored
            reference is unusable — the server returns it deliberately so a reviewer learns a page
            exists and cannot be opened, rather than silently seeing one fewer page.
        -->
        <div class="review-fallback" *ngIf="fallbackFiles.length">
            <h4>Files that cannot be previewed</h4>
            <div class="review-file" *ngFor="let file of fallbackFiles; let i = index">
                <mat-icon>description</mat-icon>
                <a *ngIf="file.url" [href]="file.url" target="_blank" rel="noopener">
                    {{ fileName(file, i) }}
                </a>
                <span *ngIf="!file.url" class="review-file-broken">
                    {{ fileName(file, i) }} — this page is stored but cannot be opened.
                </span>
            </div>
        </div>

        <p class="review-empty" *ngIf="!gallery.length && !fallbackFiles.length">
            There are no current files on this request.
        </p>

        <!--
            What a rejection superseded. A SEPARATE list, never mixed in with the live pages:
            showing a reviewer pages the patient has already replaced is how a document gets
            rejected twice for the same reason.
        -->
        <div class="review-superseded" *ngIf="supersededFiles.length">
            <h4>Replaced after an earlier rejection</h4>
            <div class="review-file" *ngFor="let file of supersededFiles; let i = index">
                <mat-icon>history</mat-icon>
                <span>{{ fileName(file, i) }}</span>
            </div>
        </div>

        <!-- The clinician's OWN attachments. A separate table and a separate person. -->
        <div class="review-validation-files" *ngIf="validationFiles.length">
            <h4>Attached by the practice</h4>
            <div class="review-file" *ngFor="let file of validationFiles; let i = index">
                <mat-icon>attach_file</mat-icon>
                <a *ngIf="file.url" [href]="file.url" target="_blank" rel="noopener">
                    {{ fileName(file, i) }}
                </a>
                <span *ngIf="!file.url">{{ fileName(file, i) }}</span>
            </div>
        </div>

        <!-- ── STAGE ONE ─────────────────────────────────────────────────────────────── -->

        <ng-container *ngIf="canReview">
            <p class="review-lead">
                Is this the right document, complete and legible? Signing it off passes it to a
                clinician to validate onto the timeline.
            </p>

            <mat-form-field appearance="outline" class="full-width" *ngIf="rejecting">
                <mat-label>What is wrong with it?</mat-label>
                <textarea matInput rows="3" [(ngModel)]="rejectNote" name="rejectNote"
                    data-test="review-reject-note"
                    placeholder="e.g. Page 2 is cut off and the signature block is missing."></textarea>
                <mat-hint>
                    Required. The patient is asked again, and whoever contacts them needs to say
                    what to send back.
                </mat-hint>
                <mat-error *ngIf="rejectNoteError">{{ rejectNoteError }}</mat-error>
            </mat-form-field>
            <p class="review-error" *ngIf="rejectNoteError && rejecting">{{ rejectNoteError }}</p>
        </ng-container>

        <!-- ── STAGE TWO ─────────────────────────────────────────────────────────────── -->

        <ng-container *ngIf="canValidate">
            <!--
                TWO DECISIONS, ONE PLACE. The clinician is reading the document; they either put it
                on the record or send it back. Splitting those across two screens would ask them to
                leave the thing they are looking at in order to say what is wrong with it.
            -->
            <p class="review-lead">
                What you write here goes on the patient's clinical timeline, under your name.
            </p>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>What did you observe? (optional)</mat-label>
                <textarea matInput rows="4" [(ngModel)]="validationNote" name="validationNote"
                    data-test="review-validation-note"
                    placeholder="e.g. Pain score 7/10, reports the swelling has not settled since the last visit."></textarea>
                <mat-hint>
                    Optional. If you are sending it back instead, say what is wrong with it -
                    that note is required.
                </mat-hint>
            </mat-form-field>

            <!--
                `[pasteEnabled]="false"` is MANDATORY inside a dialog: the component registers a
                DOCUMENT-level paste listener, which here would steal paste from the observations
                textarea directly above it.

                `showGallery` is left at its default true and `shouldRefresh` is not set, which is
                what makes the attachments render as a gallery — the component accumulates into its
                own array and draws it.
            -->
            <app-file-upload class="full-width review-upload" [fileObjects]="[]"
                [allowMultiple]="true" [allowedFileTypes]="'image/*, application/pdf'"
                [pasteEnabled]="false" [cameraEnabled]="true" [enableEditing]="true"
                title="Attach images (optional)"
                (fileUpload)="onValidationFilesPicked($event)"
                (fileDelete)="onValidationFileDeleted($event)"
                (filesEdited)="onValidationFilesEdited($event)"
                hintMessage="Drag images here, take a photograph, or click to choose">
            </app-file-upload>

            <p class="review-error" *ngIf="validationError">{{ validationError }}</p>
        </ng-container>

        <!--
            Never a dialog with no buttons and no explanation. The reasons are different and send a
            reader to different places: wait for the patient, wait for a named colleague, or
            nothing is wrong at all because it is already done.
        -->
        <p class="review-no-action" *ngIf="noActionReason" data-test="review-no-action">
            {{ noActionReason }}
        </p>
    </ng-container>
</div>

<div mat-dialog-actions class="review-actions">
    <button mat-button type="button" (click)="cancel()" [disabled]="sending">Close</button>

    <span class="review-actions-spacer"></span>

    <ng-container *ngIf="canReview">
        <ng-container *ngIf="!rejecting">
            <button mat-button type="button" color="warn" (click)="startReject()" [disabled]="sending"
                data-test="review-start-reject">
                Reject
            </button>
            <button mat-raised-button type="button" color="primary" (click)="signOff()"
                [disabled]="sending" data-test="review-sign-off">
                Sign off
            </button>
        </ng-container>
        <ng-container *ngIf="rejecting">
            <button mat-button type="button" (click)="cancelReject()" [disabled]="sending">Back</button>
            <button mat-raised-button type="button" color="warn" (click)="reject()"
                [disabled]="sending" data-test="review-confirm-reject">
                Reject and re-ask
            </button>
        </ng-container>
    </ng-container>

    <ng-container *ngIf="canValidate">
        <button mat-button type="button" color="warn" (click)="sendBack()" [disabled]="sending"
            data-test="review-send-back">
            Send back
        </button>
        <button mat-raised-button type="button" color="primary" (click)="validate()"
            [disabled]="sending" data-test="review-validate">
            Validate to timeline
        </button>
    </ng-container>
</div>
